import React, { useState, useEffect } from "react";
import { Typography, Tooltip, Button, Dropdown } from "antd";
import {
  EditOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  QuestionOutlined,
  DownOutlined,
  LockOutlined,
  HistoryOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import ActionsTable from "../../components/templates/ActionsTable";
import FullName from "../../components/tables/FullName";
import UnpaidReason from "../../components/tables/UnpaidReason";
import { ReactComponent as ClientsImg } from "../../assets/no-clients.svg";
import Currency from "../../components/tables/Currency";
import Header from "./components/Header";
import EditProspectusLine from "./components/EditProspectusLine";
import MissingHoursModal from "./components/MissingHoursModal";
import UnpaidHoursModal from "./components/UnpaidHoursModal";
import CreateProspectusFromLineForm from "./components/CreateProspectusFromLineForm";
import {
  loadProspectuseslines,
  cancelLine,
  deleteLine,
  loadProspectus,
} from "./prospectusSlice";
import { isAllowedFeature } from "../organization/organizationSlice";
import { getProspectusLine } from "../../api/server";
import { usePermission } from "../../hooks/accessControl";
import LineActionsModal from "./components/LineActionsModal";
import { useSuperAdmin } from "../../hooks/accessControl";

const { Text } = Typography;

const Prospectus = (props) => {
  const { onHide } = props;
  let location = useLocation();
  const isSuperAdmin = useSuperAdmin();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { prospectuslines } = useSelector((state) => state.prospectus);
  const { current, pageSize, total, items } = prospectuslines;
  const textFilter = useSelector((state) => state.prospectus.textFilter);
  const loading = useSelector(
    (state) => state.pendingActions["prospectus/loadProspectusLines"]
  );
  const activeProspectus = useSelector(
    (state) => state.prospectus.activeProspectus
  );
  const { activeProspectusId, activeProspectusBranch } = useSelector(
    (state) => state.prospectus
  );
  const [prospectusLine, setProspectusLine] = useState(null);
  const [fieldName, setFieldName] = useState(null);
  const [editProspectusVisible, setEditProspectusVisible] = useState(false);
  const [missingHoursVisible, setMissingHoursVisible] = useState(false);
  const [unpaidHoursVisible, setUnpaidHoursVisible] = useState(false);
  const [
    createProspectusFromLineFormVisible,
    setCreateProspectusFromLineFormVisible,
  ] = useState(false);

  const [prospectusLineActions, setProspectusLineActions] = useState(null);

  useEffect(() => {
    if (activeProspectusId && activeProspectusBranch) {
      dispatch(loadProspectus());
    }
  }, [activeProspectusId, activeProspectusBranch]);

  const onEditProspectus = async (record, name) => {
    await fetchProspectusLine(record);
    setFieldName(name);
    setEditProspectusVisible(true);
  };

  const hideEditProspectus = () => {
    dispatch(loadProspectuseslines());
    setEditProspectusVisible(false);
    setProspectusLine(null);
  };

  const onEditMissingHoursReason = async (record) => {
    await fetchProspectusLine(record);
    setMissingHoursVisible(true);
  };
  const onEditUnpaidHoursReason = async (record) => {
    await fetchProspectusLine(record);
    setUnpaidHoursVisible(true);
  };

  const fetchProspectusLine = async (record) => {
    const params = {
      lineId: record.id,
      prospectusId: record.prospectus_id,
      branchId: activeProspectus.branch_id,
    };
    const line = await getProspectusLine(params);
    return setProspectusLine(line.data);
  };

  const hideEditMissingHoursReason = () => {
    dispatch(loadProspectuseslines());
    setMissingHoursVisible(false);
    setProspectusLine(null);
  };

  const hideEditUnpaidHoursReason = () => {
    dispatch(loadProspectuseslines());
    setUnpaidHoursVisible(false);
    setProspectusLine(null);
  };

  const hideCreateProspectusFromLineForm = () => {
    dispatch(loadProspectuseslines());
    setCreateProspectusFromLineFormVisible(false);
    setProspectusLine(null);
  };

  const onCreateProspectusFromLine = async (record) => {
    setCreateProspectusFromLineFormVisible(true);
    setProspectusLine(record);
  };

  const hideLineActionsModal = () => {
    setProspectusLineActions(null);
  };

  const onShowLineActionsModal = async (record) => {
    setProspectusLineActions(record.prospectus_line_actions);
  };

  const onCancelRow = async (record) => {
    const action = await dispatch(
      cancelLine({
        lineId: record.id,
        prospectusId: record.prospectus_id,
        branchId: activeProspectus.branch_id,
      })
    );
    if (!action.error) {
      dispatch(loadProspectuseslines());
    }
  };

  const onDeleteRow = async (record) => {
    const action = await dispatch(
      deleteLine({
        lineId: record.id,
        prospectusId: record.prospectus_id,
        branchId: activeProspectus.branch_id,
      })
    );
    if (!action.error) {
      dispatch(loadProspectuseslines());
    }
  };

  const handleMenuClick = (e, record) => {
    switch (e.key) {
      case "1":
        onEditProspectus(record, "reclaimed_units");
        break;
      case "2":
        onEditProspectus(record, "refund_amount");

        break;
      case "3":
        onEditProspectus(record, "paid_amount");
        break;
      case "4":
        onEditMissingHoursReason(record);
        break;
      case "5":
        onShowLineActionsModal(record);
        break;
      case "6":
        onDeleteRow(record);
        break;
      case "7":
        onEditUnpaidHoursReason(record);
        break;
      // case "5":
      //   onCancelRow(record);
      default:
        break;
    }
  };

  const handleClosedProspectusMenuClick = (e, record) => {
    switch (e.key) {
      case "1":
        onCreateProspectusFromLine(record);
        break;
      case "2":
        onShowLineActionsModal(record);
        break;
      default:
        break;
    }
  };

  const branchPath = location.pathname.substring(
    0,
    location.pathname.lastIndexOf("/")
  );
  const hasAdminPermission = usePermission(
    activeProspectus?.branch_id,
    "accounting",
    "admin"
  );
  const hasEditorPermission = usePermission(
    activeProspectus?.branch_id,
    "accounting",
    "editor"
  );

  const hasRefunderPermission = usePermission(
    activeProspectus?.branch_id,
    "accounting",
    "refunder"
  );

  const getMenu = (record) => {
    const menuItems = [
      {
        label: intl.formatMessage({
          id: "app.prospectuses.editPendingAmount",
        }),
        key: "1",
        icon: <ReloadOutlined />,
        disabled: !hasEditorPermission,
      },
      {
        label: intl.formatMessage({
          id: "app.prospectuses.editRefundAmount",
        }),
        key: "2",
        icon: <MinusCircleOutlined />,
        disabled: !hasRefunderPermission,
      },
      {
        label: intl.formatMessage({
          id: "app.prospectuses.editPaidAmount",
        }),
        key: "3",
        icon: <EditOutlined />,
        disabled: !hasEditorPermission,
      },
      {
        label: intl.formatMessage({
          id: "app.prospectuses.missingHoursReason",
        }),
        key: "4",
        icon: <QuestionOutlined />,
        disabled: !hasEditorPermission,
      },
      {
        label: intl.formatMessage({
          id: "app.prospectuses.unpaidHoursReason",
        }),
        key: "7",
        icon: <QuestionOutlined />,
        disabled: !hasEditorPermission,
      },
      ...[
        record?.prospectus_line_actions?.length > 0
          ? {
              label: intl.formatMessage({
                id: "app.prospectuses.line_actions.changes_history",
              }),
              key: "5",
              icon: <HistoryOutlined />,
            }
          : null,
      ],
      ...[
        isSuperAdmin
          ? {
              label: intl.formatMessage({
                id: "app.prospectuses.line_actions.delete",
              }),
              key: "6",
              icon: <DeleteOutlined style={{ color: "red" }} />,
            }
          : null,
      ],

      // {
      //   label: intl.formatMessage({
      //     id: "app.prospectuses.cancelRow",
      //   }),
      //   key: "5",
      //   icon: <CloseOutlined />,
      // },
    ];

    const items = {
      items: menuItems,
      onClick: (e) => handleMenuClick(e, record),
    };
    return items;
  };

  const getClosedProspectusMenu = (record) => {
    const menuItems = [
      {
        label: intl.formatMessage({
          id: "app.prospectuses.create_prospectus_for_line",
        }),
        key: "1",
        icon: <ReloadOutlined />,
        disabled: !hasEditorPermission,
      },

      ...[
        record?.prospectus_line_actions?.length > 0
          ? {
              label: intl.formatMessage({
                id: "app.prospectuses.line_actions.changes_history",
              }),
              key: "2",
              icon: <HistoryOutlined />,
            }
          : null,
      ],
    ];
    const items = {
      items: menuItems,
      onClick: (e) => handleClosedProspectusMenuClick(e, record),
    };
    return items;
  };

  const columns =
    activeProspectus && activeProspectus.type === "refund"
      ? [
          {
            title: "line_number",
            width: "70px",
          },
          {
            title: "patient_name",
            ellipsis: true,
            render: (r) => {
              return r.record &&
                r.record["order"] &&
                r.record["order"].patient ? (
                <FullName
                  record={r.record["order"].patient}
                  customLocation={`${branchPath}/patients/${r.record["order"].patient.id}`}
                />
              ) : null;
            },
          },
          {
            title: "patient_local_id_number",
            responsive: ["xl"],
            render: (r) => {
              return (
                <Text ellipsis>
                  {r.record["order"].patient.local_id_number}
                </Text>
              );
            },
          },
          {
            title: "employee_name",
            ellipsis: true,
            render: (r) => {
              return r.record && r.record["employee"] ? (
                <FullName
                  record={r.record["employee"]}
                  customLocation={`${branchPath}/employees/${r.record["employee"].id}`}
                />
              ) : null;
            },
          },
          {
            title: "refund_units",
            responsive: ["xxl"],
            render: (r) => {
              return (
                <Text
                  ellipsis
                  style={{ direction: "ltr", unicodeBidi: "bidi-override" }}
                >
                  {r.record["charged_units"]}
                </Text>
              );
            },
          },
          {
            title: "tariff",
            responsive: ["xxl"],
            render: (r) => {
              return (
                <Currency maximumFractionDigits={4} text={r.record["tariff"]} />
              );
            },
          },
          {
            title: "reclaimed_amount",
            responsive: ["xl"],
            render: (r) => {
              return (
                <Currency
                  maximumFractionDigits={4}
                  text={r.record["reclaimed_amount"]}
                />
              );
            },
          },
          {
            title: "refund_amount",
            responsive: ["xl"],
            render: (r) => {
              return (
                <Tooltip color={"red"} title={r.record["refund_reason"]}>
                  <Text>
                    {new Intl.NumberFormat("he", {
                      style: "currency",
                      currency: "ILS",
                      maximumFractionDigits: 2,
                    }).format(r.record["refund_amount"])}
                  </Text>
                </Tooltip>
              );
            },
          },
          {
            title: "total_refund",
            responsive: ["lg"],
            render: (r) => (
              <Currency
                text={r.record["charged_amount"]}
                maximumFractionDigits={4}
              />
            ),
          },
        ]
      : [
          {
            title: "line_number",
            width: "70px",
          },
          {
            title: "patient_name",
            ellipsis: true,
            render: (r) => {
              return r.record &&
                r.record["order"] &&
                r.record["order"].patient ? (
                <FullName
                  record={r.record["order"].patient}
                  customLocation={`${branchPath}/patients/${r.record["order"].patient.id}`}
                />
              ) : null;
            },
          },
          {
            title: "patient_local_id_number",
            responsive: ["xl"],
            render: (r) => {
              return (
                <Text ellipsis>
                  {r.record["order"].patient.local_id_number}
                </Text>
              );
            },
          },
          {
            title: "employee_name",
            ellipsis: true,
            render: (r) => {
              return r.record && r.record["employee"] ? (
                <FullName
                  record={r.record["employee"]}
                  customLocation={`${branchPath}/employees/${r.record["employee"].id}`}
                />
              ) : r.record?.order?.order_service_type?.supporting_community ? (
                <Text>
                  {intl.formatMessage({
                    id: "app.scheduling.supporting_community",
                  })}
                </Text>
              ) : null;
            },
          },
          {
            title: "charged_units",
            responsive: ["xxl"],
            render: (r) => {
              return <Text ellipsis>{r.record["charged_units"]}</Text>;
            },
          },
          {
            title: "reclaimed_units",
            responsive: ["xxl"],
            render: (r) => {
              return <Text ellipsis>{r.record["reclaimed_units"]}</Text>;
            },
          },
          {
            title: "tariff",
            responsive: ["xxl"],
            render: (r) => {
              return (
                <Currency maximumFractionDigits={4} text={r.record["tariff"]} />
              );
            },
          },
          {
            title: "charged_amount",
            responsive: ["lg"],
            render: (r) => (
              <Currency
                text={r.record["charged_amount"]}
                maximumFractionDigits={4}
              />
            ),
          },
          {
            title: "refund_amount",
            responsive: ["xl"],
            render: (r) => {
              return (
                <Tooltip color={"red"} title={r.record["refund_reason"]}>
                  <Text>
                    {new Intl.NumberFormat("he", {
                      style: "currency",
                      currency: "ILS",
                      maximumFractionDigits: 2,
                    }).format(r.record["refund_amount"])}
                  </Text>
                </Tooltip>
              );
            },
          },
          {
            title: "paid_amount",
            responsive: ["xl"],
            render: Currency,
          },
          {
            title: "reclaimed_amount",
            responsive: ["xl"],
            render: (r) => {
              return (
                <Currency
                  maximumFractionDigits={4}
                  text={r.record["reclaimed_amount"]}
                />
              );
            },
          },
          {
            title: "balance",
            responsive: ["lg"],
            ellipsis: true,
            render: (r) => {
              return (
                <Currency
                  text={r.record["balance"]}
                  paid_minutes
                  alert={r.record["balance"] > 0}
                />
              );
            },
          },
          {
            title: "over_payment_amount",
            responsive: ["md"],
            ellipsis: true,
            width: "120px",
            render: (r) => <Currency text={r.record["over_payment_amount"]} />,
          },
          {
            title: "btl_error_code",
            responsive: ["xxl"],
            render: (r) => {
              return (
                <div style={{ display: "flex" }}>
                  <Tooltip title={r.record["btl_error_description"]}>
                    <Text>{r.record["btl_error_code"]}</Text>
                  </Tooltip>
                </div>
              );
            },
          },

          {
            title: "unpaid_hours_reason",
            responsive: ["lg"],
            render: (r) => {
              return <UnpaidReason text={r.record["unpaid_hours_reason_id"]} />;
            },
          },

          {
            title: "actions",
            responsive: ["lg"],
            width: "140px",
            render: ({ record }) => {
              const menu = getMenu(record);
              const closedProspectusMenu = getClosedProspectusMenu(record);
              return (activeProspectus && activeProspectus.locked) ||
                record.locked ? (
                hasAdminPermission ? (
                  <Dropdown trigger={["click"]} menu={closedProspectusMenu}>
                    <Button type="link">
                      {intl.formatMessage({
                        id: "app.salary.options",
                      })}
                      <DownOutlined />
                    </Button>
                  </Dropdown>
                ) : (
                  <LockOutlined style={{ color: "#52c41a" }} />
                )
              ) : record && record.canceled ? (
                <Text style={{ color: "red" }}>
                  {intl.formatMessage({
                    id: "app.prospectuses.canceledRow",
                  })}
                </Text>
              ) : (
                <Dropdown trigger={["click"]} menu={menu}>
                  <Button type="link">
                    {intl.formatMessage({
                      id: "app.salary.options",
                    })}
                    <DownOutlined />
                  </Button>
                </Dropdown>
              );
            },
          },
        ];

  if (isAllowedFeature("accounting_tag")) {
    columns.splice(13, 0, {
      title: "accounting_tag",
      dataIndex: "accounting_tag",
      render: (r) => {
        return <Text ellipsis>{r.record["accounting_tag"]}</Text>;
      },
      width: "110px",
    });
  }

  return (
    <div>
      <Header
        disabled={!items || items.length === 0}
        prospectuslines={items}
        onHide={onHide}
        hasAdminPermission={hasAdminPermission}
        hasEditorPermission={hasEditorPermission}
      />
      <div style={{ height: "10px" }} />
      <ActionsTable
        tableItems={columns}
        deep={true}
        category={"prospectuses"}
        resource="prospectuses"
        current={current}
        pageSize={pageSize}
        total={total}
        loading={loading}
        textFilter={textFilter}
        reload={loadProspectuseslines}
        loadPage={loadProspectuseslines}
        items={items}
        showheader={false}
        emptyImage={<ClientsImg />}
        emptytext={""}
      />

      <EditProspectusLine
        visible={editProspectusVisible}
        hide={hideEditProspectus}
        prospectusLine={prospectusLine}
        fieldName={fieldName}
      />
      <MissingHoursModal
        visible={missingHoursVisible}
        hide={hideEditMissingHoursReason}
        prospectusLine={prospectusLine}
      />
      <UnpaidHoursModal
        visible={unpaidHoursVisible}
        hide={hideEditUnpaidHoursReason}
        prospectusLine={prospectusLine}
      />
      <CreateProspectusFromLineForm
        visible={createProspectusFromLineFormVisible}
        hide={hideCreateProspectusFromLineForm}
        prospectusLine={prospectusLine}
      />
      <LineActionsModal
        visible={prospectusLineActions !== null}
        hide={hideLineActionsModal}
        line_actions={prospectusLineActions || []}
      />
    </div>
  );
};

export default Prospectus;
